import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Home as HomeIcon,
  MapPin,
  Phone,
  Ruler,
  Scissors,
  Star,
  Wrench,
} from "lucide-react";
import { site } from "@/config/site";
import { collections, IMAGES } from "@/data/products";
import { benefits, homeFaqs, projects, reviews, steps, whyPoints } from "@/data/content";
import { useLeadForm } from "@/components/lead/LeadForm";
import { trackEvent } from "@/lib/tracking";
import { SEO, localBusinessLd, organizationLd, faqLd } from "@/components/site/SEO";
import { Reveal, MaskedLines, Eyebrow } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";
import { ProductCard } from "@/components/site/ProductCard";
import { FAQ } from "@/components/site/FAQ";
import { CTASection } from "@/components/site/CTASection";

const icons = { Home: HomeIcon, Ruler, Scissors, Wrench };

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.89 1.22 3.09.15.2 2.11 3.22 5.1 4.51.71.31 1.27.49 1.7.63.72.23 1.37.2 1.88.12.58-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.83 9.83 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88a9.82 9.82 0 0 1 9.88 9.89c0 5.45-4.44 9.88-9.89 9.88m8.42-18.29A11.8 11.8 0 0 0 12.04 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.9 11.9 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.9 0-3.18-1.24-6.16-3.48-8.4" />
  </svg>
);

const HERO_VIDEO =
  "https://videos.pexels.com/video-files/7614535/7614535-hd_1920_1080_30fps.mp4";
const HERO_POSTER =
  "https://images.pexels.com/videos/7614535/pexels-photo-7614535.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Bento: first two cards span 3 cols, middle six span 2, last two span 3 — all rows aligned.
const spanFor = (i) => (i < 2 || i >= 8 ? "lg:col-span-3" : "lg:col-span-2");

export default function Home() {
  const { openForm } = useLeadForm();

  return (
    <>
      <SEO
        title="Made-to-Measure Blinds Birmingham | My Blinds Company"
        description="Made-to-measure blinds and shutters for homes and businesses across Birmingham and the West Midlands. Free measuring, quotations and professional fitting. Book your home visit today."
        path="/"
        jsonLd={[localBusinessLd, organizationLd, faqLd(homeFaqs)]}
      />

      {/* HERO — full-bleed video with masked headline */}
      <section
        className="relative overflow-hidden bg-navy-950 grain"
        data-testid="hero-section"
      >
        <motion.video
          initial={{ scale: 1.06, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onCanPlay={(e) => e.currentTarget.play().catch(() => {})}
          poster={HERO_POSTER}
          aria-hidden="true"
        >
          <source src={HERO_VIDEO} type="video/mp4" media="(min-width: 768px)" />
        </motion.video>
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/90 via-navy-950/55 to-navy-950/20" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-navy-950/85 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full min-h-[92vh] flex flex-col justify-center py-24 sm:py-32">
          {site.offer?.enabled && (
            <motion.button
              type="button"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              onClick={() => openForm({ source: "hero_offer" })}
              data-testid="hero-offer-pill"
              className="mb-5 inline-flex w-fit items-center gap-2 rounded-full bg-[#C9A227] px-5 py-2 text-xs font-bold uppercase tracking-[0.14em] text-navy-950 shadow-xl transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110"
            >
              {site.offer.text} — {site.offer.cta}
            </motion.button>
          )}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex w-fit items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white mb-7"
          >
            <MapPin className="h-3.5 w-3.5" /> Birmingham & Surrounding Areas
          </motion.p>
          <h1 className="font-serif font-semibold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.08] max-w-3xl">
            <MaskedLines lines={["Made-to-Measure", "Blinds & Shutters"]} />
            <MaskedLines lines={["in Birmingham"]} className="italic text-warm-200" />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 text-base sm:text-lg leading-relaxed text-white/75 max-w-xl"
          >
            Stylish made-to-measure blinds and shutters for homes and
            businesses across Birmingham and the West Midlands. Book your
            free home measure and no-obligation quotation today.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <button
              type="button"
              data-testid="hero-book-visit-btn"
              onClick={() => openForm({ source: "hero" })}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-navy-900 shadow-2xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-white/20"
            >
              Book My Free Home Measure <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              data-testid="hero-quote-btn"
              onClick={() => openForm({ source: "hero_quote" })}
              className="inline-flex items-center justify-center rounded-full border border-white/40 bg-white/5 backdrop-blur px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-white hover:text-navy-900"
            >
              Get a Free Quote
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-4 flex flex-wrap items-center gap-3"
          >
            <a
              href={site.phone.href}
              data-testid="hero-call-btn"
              onClick={() => trackEvent("phone_click", { location: "hero" })}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold text-white/85 border border-white/20 hover:bg-white/10 transition-colors"
            >
              <Phone className="h-3.5 w-3.5" /> Call My Blinds Company
            </a>
            <a
              href={`${site.whatsapp.href}?text=${encodeURIComponent("Hi My Blinds Company — I'd like a free quote for blinds.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              onClick={() => trackEvent("whatsapp_click", { location: "hero" })}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold text-white/85 border border-white/20 hover:bg-white/10 transition-colors"
            >
              <WhatsAppIcon className="h-3.5 w-3.5" /> WhatsApp Us
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3"
          >
            <p className="text-xs sm:text-sm text-white/70 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-warm-200" /> Free Measure &amp; Quote</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-warm-200" /> Made to Measure</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-warm-200" /> Professional Fitting</span>
            </p>
            <span
              className="inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/10 backdrop-blur px-4 py-2"
              data-testid="hero-google-rating"
            >
              <span className="flex gap-0.5" aria-hidden="true">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-[#C9A227] text-[#C9A227]" />
                ))}
              </span>
              <span className="text-xs font-semibold text-white">
                Rated {site.google.rating} on Google
              </span>
              {site.google.reviewCount && (
                <span className="text-[11px] text-white/60">from {site.google.reviewCount} reviews</span>
              )}
            </span>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-10 font-serif italic text-lg text-white/55"
          >
            {site.tagline}
          </motion.p>
        </div>
      </section>

      <Marquee />

      {/* BENEFITS */}
      <section className="py-16 sm:py-24" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => {
              const Icon = icons[b.icon];
              return (
                <Reveal key={b.title} delay={i * 0.08}>
                  <div className="h-full rounded-2xl border border-warm-200 bg-white p-7 shadow-sm hover:shadow-lg hover:border-navy-800/20 transition-all duration-300">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-navy-900 mb-5">
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <h2 className="font-sans text-lg font-semibold text-slate-900 mb-2">{b.title}</h2>
                    <p className="text-sm leading-relaxed text-slate-600">{b.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="py-16 sm:py-24 bg-warm-100/60" data-testid="collections-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-12">
            <Eyebrow>Our Collections</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Every style, made to your windows
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              From clean-lined rollers to full-height shutters, every product
              is made to measure and fitted by our own specialists.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6">
            {collections.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.08} className={spanFor(i)}>
                <ProductCard product={p} className="h-full" eager={i < 2} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-16 sm:py-24 lg:py-32 bg-navy-950 grain relative overflow-hidden" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow className="text-white/60">Why Choose Us</Eyebrow>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">
                Why homeowners choose us over ordering online
              </h2>
              <p className="mt-5 text-base leading-relaxed text-white/70">
                Ordering blinds online means measuring yourself, guessing at
                colours from a screen and hoping it fits. We take care of the
                whole thing — advice, measuring, making and fitting — so the
                result is right first time.
              </p>
              <button
                type="button"
                data-testid="why-book-visit-btn"
                onClick={() => openForm({ source: "why_choose_home" })}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-navy-900 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
              >
                {site.primaryCTA} <ArrowRight className="h-4 w-4" />
              </button>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {whyPoints.slice(0, 6).map((w, i) => (
              <Reveal key={w.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 hover:bg-white/10 transition-colors duration-300">
                  <h3 className="font-sans text-base font-semibold text-white mb-1.5">{w.title}</h3>
                  <p className="text-sm leading-relaxed text-white/60">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 sm:py-24 lg:py-32" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              From first visit to final fitting
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="relative">
                  <span className="font-serif italic text-6xl text-warm-300 select-none">{s.n}</span>
                  <h3 className="mt-3 font-sans text-lg font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
                  {i < steps.length - 1 && (
                    <span className="hidden lg:block absolute top-8 left-full w-10 border-t border-dashed border-warm-300" aria-hidden="true" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <button
              type="button"
              data-testid="how-it-works-book-btn"
              onClick={() => openForm({ source: "how_it_works" })}
              className="inline-flex items-center gap-2 rounded-full bg-navy-900 px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-navy-900/10 transition-all duration-300 hover:bg-navy-800 hover:-translate-y-0.5"
            >
              Book My Free Appointment <ArrowRight className="h-4 w-4" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* OUR WORK PREVIEW */}
      <section className="py-16 sm:py-24 bg-warm-100/60" data-testid="our-work-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <Reveal>
              <Eyebrow>Inspiration</Eyebrow>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
                Styles we design, measure and fit
              </h2>
            </Reveal>
            <Reveal>
              <Link
                to="/our-work"
                data-testid="our-work-view-all"
                className="inline-flex items-center gap-2 text-sm font-semibold text-navy-900 hover:text-navy-800"
              >
                View Our Work <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {projects.slice(0, 4).map((p, i) => (
              <Reveal key={p.title + i} delay={i * 0.06}>
                <Link to="/our-work" className="group block overflow-hidden rounded-2xl relative aspect-[3/4]">
                  <img
                    src={p.image}
                    alt={`${p.title} in a ${p.room.toLowerCase()}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950/70 via-transparent to-transparent" />
                  <div className="absolute bottom-0 p-4">
                    <p className="text-white font-serif text-lg leading-tight">{p.title}</p>
                    <p className="text-white/70 text-xs mt-1">{p.room}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-16 sm:py-24" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-10 text-center">
            <Eyebrow className="justify-center">Customer Reviews</Eyebrow>
            <p className="mt-5 font-serif text-6xl sm:text-7xl text-slate-900 leading-none">
              {site.google.rating}
            </p>
            <div
              className="mt-3 flex justify-center gap-1"
              role="img"
              aria-label={`Rated ${site.google.rating} out of 5 stars on Google`}
            >
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 fill-[#C9A227] text-[#C9A227]" />
              ))}
            </div>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Rated {site.google.rating} out of 5 on Google
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {site.google.reviewCount
                ? `From ${site.google.reviewCount} verified Google reviews`
                : "From our verified Google reviews"}
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <figure className="h-full rounded-2xl border border-warm-200 bg-white p-7 shadow-sm hover:shadow-lg transition-shadow duration-300">
                  <div className="flex gap-1 mb-4" aria-label={`${r.rating} out of 5 stars`}>
                    {[...Array(r.rating || 5)].map((_, s) => (
                      <Star key={s} className="h-4 w-4 fill-[#C9A227] text-[#C9A227]" />
                    ))}
                  </div>
                  <blockquote className="text-sm leading-relaxed text-slate-700">
                    “{r.text}”
                  </blockquote>
                  <figcaption className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {r.name} <span className="font-normal normal-case text-slate-400">· {r.source}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          {site.social.googleReviews && (
            <Reveal className="mt-8 text-center">
              <a
                href={site.social.googleReviews}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-reviews-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-navy-900 hover:text-navy-800"
              >
                Read our reviews on Google <ArrowRight className="h-4 w-4" />
              </a>
            </Reveal>
          )}
        </div>
      </section>

      {/* RESIDENTIAL / SHUTTERS / COMMERCIAL */}
      <section className="py-16 sm:py-24 bg-warm-100/60" data-testid="audiences-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-12">
            <Eyebrow>Homes &amp; Business</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Blinds for homes, landlords &amp; businesses
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: HomeIcon,
                title: "For Your Home",
                text: "Transform your home with made-to-measure blinds — expert advice in the comfort of your home, made to fit your windows, professionally installed.",
                link: "/blinds",
                cta: "Explore Residential Blinds",
                testid: "audience-residential",
              },
              {
                icon: Scissors,
                title: "Plantation Shutters",
                text: "Timeless shutters, measured and fitted precisely to your windows — from café-style privacy to full-height statement looks.",
                link: "/shutters",
                cta: "Explore Shutters",
                testid: "audience-shutters",
              },
              {
                icon: Building2,
                title: "For Your Business",
                text: "Commercial blinds across Birmingham & the West Midlands — offices, shops, salons, clinics, landlords and property managers.",
                link: "/commercial-blinds-birmingham",
                cta: "Commercial Blinds",
                testid: "audience-commercial",
              },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <Link
                  to={c.link}
                  data-testid={c.testid}
                  className="group flex h-full flex-col rounded-2xl border border-warm-200 bg-white p-8 shadow-sm transition-all duration-300 hover:shadow-xl hover:border-navy-800/20"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-navy-900 mb-6">
                    <c.icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-serif text-2xl text-slate-900 mb-2">{c.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600 flex-1">{c.text}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy-900 group-hover:gap-3 transition-all">
                    {c.cta} <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LOCAL BIRMINGHAM */}
      <section className="py-16 sm:py-24 bg-warm-100/60" data-testid="local-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] aspect-[4/3] shadow-xl">
              <img
                src={IMAGES.bayWindow}
                alt="Elegant bay window dressed with made-to-measure shutters in a Birmingham home"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Local Specialists</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Birmingham's local made-to-measure blinds specialists
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              As a Birmingham company, we provide home consultations,
              professional measuring and expert fitting right across the city
              and the wider West Midlands. You deal with local people
              throughout — from the first visit to the final fitting check.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Not sure whether we cover your road? Send us your postcode and
              we'll confirm straight away.
            </p>
            <Link
              to="/areas-we-cover"
              data-testid="view-areas-link"
              className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-navy-900 hover:text-navy-800"
            >
              View Areas We Cover <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-24" id="faqs" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <Eyebrow className="justify-center">Questions, Answered</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Frequently asked questions
            </h2>
          </Reveal>
          <FAQ items={homeFaqs} idPrefix="home-faq" />
          <Reveal className="mt-10 text-center">
            <p className="text-sm text-slate-600 mb-5">
              Still unsure? A free home visit answers everything — with samples
              in hand.
            </p>
            <button
              type="button"
              data-testid="faq-book-visit-btn"
              onClick={() => openForm({ source: "faq_section" })}
              className="inline-flex items-center gap-2 rounded-full bg-navy-900 px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-navy-900/10 transition-all duration-300 hover:bg-navy-800 hover:-translate-y-0.5"
            >
              {site.primaryCTA} <ArrowRight className="h-4 w-4" />
            </button>
          </Reveal>
        </div>
      </section>

      <CTASection source="home_final_cta" />
    </>
  );
}
